import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  SmartToy as AgentIcon
} from '@mui/icons-material';
import { useDeleteAgentMutation } from '../../../store/slices/apiSlice';

const AgentDeleteModal = ({ open, onClose, agent }) => {
  const [deleteAgent, { isLoading }] = useDeleteAgentMutation();
  const [error, setError] = useState('');

  const handleDelete = async () => {
    try {
      setError('');
      await deleteAgent(agent.id).unwrap();
      onClose();
    } catch (err) {
      setError(err.data?.message || 'Une erreur est survenue lors de la suppression');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  // Fonction pour obtenir l'icône du type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'whatsapp':
        return '💬';
      case 'telegram':
        return '📱';
      case 'discord':
        return '🎮';
      case 'web':
        return '🌐';
      default:
        return '🤖';
    }
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'training':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!agent) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <WarningIcon color="error" />
          <Typography variant="h6">
            Confirmer la suppression
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Êtes-vous sûr de vouloir supprimer cet agent ?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cette action est <strong>irréversible</strong> et supprimera définitivement :
          </Typography>
        </Box>

        {/* Informations de l'agent */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            mb: 3,
            bgcolor: 'grey.50'
          }}
        >
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 48,
                height: 48
              }}
            >
              {getTypeIcon(agent.type)}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6" gutterBottom>
                {agent.name}
              </Typography>
              <Box display="flex" gap={1} alignItems="center">
                <Chip
                  label={agent.type}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
                <Chip
                  label={agent.status}
                  color={getStatusColor(agent.status)}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
            </Box>
          </Box>

          {agent.description && (
            <Typography variant="body2" color="text.secondary">
              {agent.description}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Avertissements */}
        <Box sx={{ bgcolor: 'error.50', p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="error.main" gutterBottom>
            ⚠️ Données qui seront supprimées :
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Configuration de l'agent (modèle IA, prompts, paramètres)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Historique des conversations
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Métriques et statistiques
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Connexions et intégrations
            </Typography>
          </Box>
        </Box>

        {/* Métriques si disponibles */}
        {agent.metrics && (
          <Box sx={{ bgcolor: 'info.50', p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="info.main" gutterBottom>
              📊 Métriques actuelles :
            </Typography>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                {agent.metrics.totalConversations || 0} conversations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {agent.metrics.totalMessages || 0} messages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {agent.metrics.successRate || 0}% de succès
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Tapez le nom de l'agent pour confirmer : <strong>{agent.name}</strong>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          startIcon={isLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          disabled={isLoading}
        >
          {isLoading ? 'Suppression...' : 'Supprimer définitivement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentDeleteModal;
